import React from 'react';
import { Box, Typography, formatPrice } from '@lucid/lego';
import { useUserIdentity } from '@lucid/portal-core';
import type { OrderDetailsResponse } from '../../../../../../api/services/lupp/order/dto.order';
import { useStore } from '../../../../../../store/store';
import { resolveNlFeeLabel } from '../../../../../../utils/tools';
import styles from './InvoiceSummary.module.scss';

type InvoiceSummaryProps = {
  invoiceSummaryDetails?: OrderDetailsResponse['invoiceSummaryDetails'];
  orderData?: OrderDetailsResponse;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/**
 * InvoiceSummary component displays the invoice breakdown
 * including pricing, taxes, payments, and amounts due.
 *
 * @component
 * @param {InvoiceSummaryProps} props - Component props
 * @returns {JSX.Element} Invoice summary section
 *
 * @example
 * ```tsx
 * <InvoiceSummary
 *   invoiceSummaryDetails={orderData.invoiceSummaryDetails}
 *   theme="dark"
 *   labels={labels}
 * />
 * ```
 */
const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({
  invoiceSummaryDetails,
  orderData,
  theme,
  labels,
  dataTestId = 'invoice-summary',
}) => {
  const locale = useStore((state) => state.locale);
  const { userIdentity } = useUserIdentity();
  const isNlPricing = userIdentity?.countryCode?.toUpperCase() === 'NL';

  const renderFees = (fees: Record<string, number> | undefined, type: string) =>
    isNlPricing &&
    Object.entries(fees ?? {}).map(([key, value]) => (
      <Box key={`${type}-fee-${key}`} className={styles.priceRow}>
        <Typography variant="body" size="small" theme={theme} color="black">
          {resolveNlFeeLabel(key, labels)}
        </Typography>
        <Typography variant="body" size="small" theme={theme} color="black">
          {formatPrice(value, { locale })}
        </Typography>
      </Box>
    ));

  const chargingVoucherValue = orderData?.configurationDetails?.chargingVoucher;
  const hasChargingVoucher =
    chargingVoucherValue !== null &&
    chargingVoucherValue !== undefined &&
    Number(chargingVoucherValue) !== 0;

  const chargingVoucherCreditValue = orderData?.configurationDetails?.chargingVoucherCredit;
  const hasChargingVoucherCredit =
    chargingVoucherCreditValue !== null &&
    chargingVoucherCreditValue !== undefined &&
    Number(chargingVoucherCreditValue) !== 0;

  return (
    <Box data-testid={dataTestId} className={styles[theme]}>
      <Box mb={24}>
        <Typography variant="body" size="large" theme={theme} color="black">
          {labels?.['invoice.summary'] ?? ''}
        </Typography>
      </Box>
      <Box className={styles.priceContainer}>
        <Box className={styles.priceRow}>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalConfigPriceExclTax'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.totalConfigPriceExclTax ?? 0, { locale })}
          </Typography>
        </Box>
        {hasChargingVoucher && (
          <Box className={styles.priceRow}>
            <Typography variant="body" size="small" theme={theme} color="black">
              {labels?.['vehicle.chargingCredit'] ?? 'Charging Credit'}
            </Typography>
            <Typography variant="body" size="small" theme={theme} color="black">
              {formatPrice(chargingVoucherValue ?? 0, { locale })}
            </Typography>
          </Box>
        )}
        {hasChargingVoucherCredit && (
          <Box className={styles.priceRow}>
            <Typography variant="body" size="small" theme={theme} color="black">
              {labels?.['vehicle.chargingCreditOffer'] ?? 'Charging Credit Offer'}
            </Typography>
            <Typography variant="body" size="small" theme={theme} color="black">
              {formatPrice(chargingVoucherCreditValue ?? 0, { locale })}
            </Typography>
          </Box>
        )}
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalCreditsDiscounts'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.totalCredits ?? 0, { locale })}
          </Typography>
        </Box>
        {renderFees(orderData?.configurationDetails?.feesTaxable, 'taxable')}
        <hr className={styles.divider} />
        <Box className={styles.priceRow}>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.subtotalOrderExclTax'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.orderSubtotalExclTax ?? 0, { locale })}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalTax'] ?? ''} {invoiceSummaryDetails?.taxPercentage}%
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.salesTax ?? 0, { locale })}
          </Typography>
        </Box>
        {renderFees(orderData?.configurationDetails?.feesNonTaxable, 'non-taxable')}
        <hr className={styles.divider} />
        <Box className={styles.priceRow}>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalOrderInclTax'] ?? ''}
          </Typography>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.totalOrderInclTax ?? 0, { locale })}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalPayments'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.totalPayments ?? 0, { locale })}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalAmountDue'] ?? ''}
          </Typography>
          <Typography variant="h5" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.totalAmountDue ?? 0, { locale })}
          </Typography>
        </Box>
        <Box className={styles.highlighted}>
          <Box className={styles.priceRow}>
            <Typography variant="body" size="small" theme={theme} color="black">
              {labels?.['invoice.summary.amountDuePriorToHandoff'] ?? ''}
            </Typography>
            <Typography variant="body" size="small" theme={theme} color="black">
              {formatPrice(invoiceSummaryDetails?.amountDuePriorToHandoff ?? 0, { locale })}
            </Typography>
          </Box>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.totalRefunds'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.totalRefunds ?? 0, { locale })}
          </Typography>
        </Box>
        <Box className={styles.priceRow}>
          <Typography variant="body" size="small" theme={theme} color="black">
            {labels?.['invoice.summary.netPayments'] ?? ''}
          </Typography>
          <Typography variant="body" size="small" theme={theme} color="black">
            {formatPrice(invoiceSummaryDetails?.netPayments ?? 0, { locale })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

InvoiceSummary.displayName = 'InvoiceSummary';

export default InvoiceSummary;
